import React, { useEffect, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useDispatch } from 'react-redux'

import { applyPopoverWidth } from '../../../../../utils/HelperFunctions'
import { colors } from '../../../../styles/global'
import useWindowSize from '../../../../../utils/useWindowSize'
import { translate } from '../../../../../i18n/TranslationService'
import ModalHeader from '../../../../UIComponents/FloatModals/ModalHeader'
import CustomScrollView from '../../../../UIControls/CustomScrollView'
import { BOT_OPTION_MODAL_ID, removeModal, storeModal } from '../../../../ModalsManager/modalsManager'
import Line from '../../../../UIComponents/FloatModals/GoalMilestoneModal/Line'
import PreConfigTasksArea from './PreConfigTasksArea'
import StopChatingOption from './StopChatingOption'
import StartChatingOption from './StartChatingOption'
import PreConfigTaskGeneratorModal from '../../../../UIComponents/FloatModals/PreConfigTaskGeneratorModal/PreConfigTaskGeneratorModal'
import { resolveAssistantForProjectObject } from '../../../../AdminPanel/Assistants/assistantsHelper'
import { setAssistantEnabled } from '../../../../../redux/actions'
import SelectAssistantsOption from './SelectAssistantsOption'
import AssistantModal from '../../../../UIComponents/FloatModals/ChangeAssistantModal/AssistantModal'
import { setSelectedNote, setTaskInDetailView } from '../../../../../redux/actions'
import { setObjectAssistantEnabled } from '../../../../../utils/assistantHelper'
import Backend from '../../../../../utils/BackendBridge'
import { normalizeAssistantObjectType, setAssistantForObject } from './objectAssistantHelper'
import { getSafeAreaModalMaxHeight } from '../../../../../utils/modalSafeArea'
import SelectModelOption from './SelectModelOption'
import ThreadAssistantModelModal from './ThreadAssistantModelModal'
import { useThreadAssistantModel } from './threadAssistantModelState'

export default function BotOptionsModal({
    objectType,
    objectId,
    closeModal,
    onSelectBotOption,
    assistantId,
    projectId,
    setAssistantId,
    inMyDay,
    inChatTab,
    parentObject,
    updateObjectState,
}) {
    const dispatch = useDispatch()
    const [selectedTask, setSelectedTask] = useState(null)
    const [showAssistants, setShowAssistants] = useState(false)
    const [showModels, setShowModels] = useState(false)
    const [linkedNoteTitle, setLinkedNoteTitle] = useState(null)
    const [width, height] = useWindowSize()
    const normalizedObjectType = normalizeAssistantObjectType(objectType)
    const {
        model: threadModel,
        isSupported: canPinThreadModel,
        updateModel: updateThreadModel,
    } = useThreadAssistantModel(projectId, objectId, normalizedObjectType)

    const linkedNoteId = parentObject?.noteId
    useEffect(() => {
        if (!linkedNoteId || !projectId) {
            setLinkedNoteTitle(null)
            return
        }
        let cancelled = false
        Backend.getNote(projectId, linkedNoteId)
            .then(note => {
                if (!cancelled) setLinkedNoteTitle(note?.title || null)
            })
            .catch(() => {
                if (!cancelled) setLinkedNoteTitle(null)
            })
        return () => {
            cancelled = true
        }
    }, [projectId, linkedNoteId])

    const assistant = resolveAssistantForProjectObject(projectId, assistantId)

    const setAssistantEnabledForObject = isEnabled => {
        setObjectAssistantEnabled(projectId, objectId, normalizedObjectType, isEnabled)
        if (parentObject) {
            const updatedObject = { ...parentObject, isAssistantEnabled: isEnabled }
            // The composer's button passes only `{ isAssistantEnabled }`; never let that stub
            // replace the real object held for the detail view (AT-2886).
            if (parentObject.id && normalizedObjectType === 'tasks') {
                dispatch(setTaskInDetailView(updatedObject))
            } else if (parentObject.id && normalizedObjectType === 'notes') {
                dispatch(setSelectedNote(updatedObject))
            }
            if (updateObjectState) {
                updateObjectState(updatedObject)
            }
        }
        dispatch(setAssistantEnabled(isEnabled))
    }

    const updateAssistant = async selectedAssistantId => {
        console.log('[BotOptionsModal] updateAssistant called:', {
            objectType,
            normalizedObjectType,
            projectId,
            objectId,
            previousAssistantId: assistantId,
            selectedAssistantId,
        })

        setAssistantId?.(selectedAssistantId)

        await setAssistantForObject(projectId, objectId, normalizedObjectType, selectedAssistantId, true)

        // Optimistic update for UI
        if (parentObject?.id) {
            const updatedObject = { ...parentObject, assistantId: selectedAssistantId }
            if (normalizedObjectType === 'tasks') {
                dispatch(setTaskInDetailView(updatedObject))
            } else if (normalizedObjectType === 'notes') {
                dispatch(setSelectedNote(updatedObject))
            }
        }

        // Optimistic update for UI via local state (prevents overwrite on tab change)
        if (parentObject && updateObjectState) {
            const updatedObject = { ...parentObject, assistantId: selectedAssistantId }
            updateObjectState(updatedObject)
        }

        if (inChatTab) {
            dispatch(setAssistantEnabled(true))
        }
        setShowAssistants(false)
    }

    const enableAssistantForObject = () => {
        if (inChatTab) {
            setAssistantEnabledForObject(true)
        } else if (!inMyDay) {
            dispatch(setAssistantEnabled(true))
        }
    }

    const toggleAssistantEnabled = async isEnabled => {
        closeModal()
        if (isEnabled && assistant?.uid && assistant.uid !== assistantId) {
            await updateAssistant(assistant.uid)
        }
        setAssistantEnabledForObject(isEnabled)
        if (onSelectBotOption) onSelectBotOption()
    }

    useEffect(() => {
        storeModal(BOT_OPTION_MODAL_ID)
        return () => {
            removeModal(BOT_OPTION_MODAL_ID)
        }
    }, [])

    return (
        <View style={[localStyles.container, applyPopoverWidth(), { maxHeight: getSafeAreaModalMaxHeight(height) }]}>
            {showAssistants ? (
                <AssistantModal
                    closeModal={() => {
                        setShowAssistants(false)
                    }}
                    projectId={projectId}
                    updateAssistant={updateAssistant}
                    currentAssistantId={assistantId || assistant?.uid}
                />
            ) : showModels ? (
                <ThreadAssistantModelModal
                    closeModal={() => {
                        setShowModels(false)
                    }}
                    selectedModel={threadModel}
                    assistantModel={assistant?.model}
                    updateModel={updateThreadModel}
                />
            ) : selectedTask ? (
                <PreConfigTaskGeneratorModal
                    projectId={projectId}
                    closeModal={() => {
                        setSelectedTask(null)
                        closeModal()
                    }}
                    task={selectedTask}
                    assistant={assistant}
                    processPromp={(prompt, options) => {
                        if (inChatTab) {
                            setAssistantEnabledForObject(true)
                        }
                        const aiSettings = {
                            model: selectedTask.aiModel,
                            reasoningEffort: selectedTask.aiReasoningEffort,
                            systemMessage: selectedTask.aiSystemMessage,
                        }
                        onSelectBotOption(prompt, selectedTask.name, aiSettings, {
                            ...options,
                            taskMetadata: {
                                ...(selectedTask.taskMetadata || {}),
                                sendWhatsApp: !!selectedTask.sendWhatsApp,
                            },
                        })
                        if (!inMyDay) dispatch(setAssistantEnabled(true))
                    }}
                    defaultContext={
                        parentObject
                            ? {
                                  name: parentObject.noteId
                                      ? linkedNoteTitle ||
                                        parentObject.title ||
                                        parentObject.name ||
                                        parentObject.displayName
                                      : parentObject.title || parentObject.name || parentObject.displayName,
                                  id: parentObject.noteId || parentObject.id || parentObject.uid,
                                  type: parentObject.noteId ? 'note' : normalizedObjectType,
                              }
                            : null
                    }
                />
            ) : (
                <CustomScrollView style={localStyles.scroll} showsVerticalScrollIndicator={false}>
                    <ModalHeader
                        closeModal={closeModal}
                        title={translate('Alldone assistant')}
                        description={translate('Using the Alldone Assistant will cost gold based on how much you chat')}
                        description2={translate('Select from the options below')}
                    />
                    {normalizedObjectType !== 'assistants' && (
                        <>
                            <SelectAssistantsOption setShowAssistants={setShowAssistants} />
                            <Line style={{ marginVertical: 4 }} />
                        </>
                    )}
                    {canPinThreadModel && (
                        <>
                            <SelectModelOption
                                threadModel={threadModel}
                                assistantModel={assistant?.model}
                                onPress={() => setShowModels(true)}
                            />
                            <Line style={{ marginVertical: 4 }} />
                        </>
                    )}
                    <StartChatingOption
                        assistant={assistant}
                        closeModal={closeModal}
                        toggleAssistantEnabled={toggleAssistantEnabled}
                        inChatTab={inChatTab}
                    />
                    <PreConfigTasksArea
                        closeModal={closeModal}
                        selectTask={setSelectedTask}
                        assistantId={assistant.uid}
                        projectId={projectId}
                        onSelectBotOption={onSelectBotOption}
                        enableAssistantForObject={enableAssistantForObject}
                        inMyDay={inMyDay}
                    />
                    {!inMyDay && (
                        <>
                            <Line style={{ marginVertical: 4 }} />
                            <StopChatingOption
                                closeModal={closeModal}
                                toggleAssistantEnabled={toggleAssistantEnabled}
                            />
                        </>
                    )}
                </CustomScrollView>
            )}
        </View>
    )
}

const localStyles = StyleSheet.create({
    container: {
        flexDirection: 'column',
        borderRadius: 4,
        backgroundColor: colors.Secondary400,
        boxShadow: '0px 4px 16px rgba(78,93,120,0.56)',
        elevation: 3,
    },
    scroll: {
        padding: 16,
    },
})
